class GOCSequenceFrame {
  constructor(options) {
    if (!options) {
      console.log('options is required');
      return;
    }
    if (!options.parentElement) {
      console.log('options.parentElement is required');
      return;
    }
    // 设备检测标志
    this.breakpoint = 768;
    this.isPc = this.checkViewportWidth(this.breakpoint);

    // 布局参数
    this.canvasWidth = 0;
    this.canvasHeight = 0;

    // 资源管理
    this.pcImageNum = 0;
    this.mobImageNum = 0;
    this.pcImageUrlList = [];
    this.mobImageUrlList = [];

    // 使用 ImageBitmap 或 Image 对象
    // ImageBitmap 支持检测：Safari 15+ / iOS 15+ / Chrome 50+ / Firefox 42+
    this.supportsImageBitmap = this.checkImageBitmapSupport();
    console.log('[GOC] ImageBitmap 支持:', this.supportsImageBitmap);
    
    // 图片缓存列表（ImageBitmap 或 Image 对象）
    this.imgHtmlList = [];
    this.isOnceUpdateImg = true; // 一次性执行控制（防重复执行）

    // 动画控制
    this.previousProgress = 0;
    this.currentFrameIndex = 0;
    this.isScrollingDown = true;
    this.isFirstRender = true; // 首次渲染控制

    // Worker实例
    this.imageLoaderWorker = null;
    
    // 预加载相关
    this.preloadedColorImages = {}; // 预加载的颜色图片缓存 { colorKey: [bitmap1, bitmap2, ...] }
    this.preloadWorkers = {}; // 预加载 Worker 实例
    
    // LRU 缓存策略（内存优化：完全不缓存，每次切换颜色都重新加载）
    this.maxCachedColors = 0; // 不缓存任何颜色，最大限度减少内存占用
    this.cacheOrder = []; // 缓存使用顺序（LRU 追踪）
    this.currentColorKey = null; // 当前正在使用的颜色（不会被释放）
    
    // 预加载队列管理（串行预加载）
    this.preloadQueue = []; // 预加载队列
    this.isPreloadingActive = false; // 是否正在预加载
    
    // 批次加载配置（内存优化：减少并发数，增加间隔让 GC 有时间回收）
    this.BATCH_SIZE = 10; // 每批加载数量（从 20 减少到 10）
    this.BATCH_DELAY = 100; // 批次间隔 (ms)（从 50 增加到 100）
    this.FIRST_LOAD_BATCH_SIZE = 10; // 首次加载也使用分批（移动端内存优化）
    
    // 懒加载状态（页面加载时不自动加载图片，只在用户点击 Explore 按钮时才加载）
    this.isInitialLoadStarted = false; // 是否已开始首次加载
    
    // 懒加载选项（默认 false，保持向后兼容）
    // 当 lazyLoad 为 true 时，init() 不会自动加载图片，需要手动调用 startLoading()
    // 当 lazyLoad 为 false 时（默认），init() 会自动加载图片
    this.lazyLoad = options.lazyLoad || false;

    // 初始化
    this.container = options.parentElement;
    this.init();
  }
  
  // 检测 ImageBitmap 支持
  checkImageBitmapSupport() {
    // 基本检测：createImageBitmap 是否存在
    if (typeof createImageBitmap !== 'function') {
      return false;
    }
    
    // Safari 版本检测（Safari 15 以下在 Worker 中使用 ImageBitmap 有问题）
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isSafari) {
      const match = navigator.userAgent.match(/Version\/(\d+)/);
      if (match && parseInt(match[1]) < 15) {
        console.log('[GOC] Safari 版本低于 15，回退到 Image 方案');
        return false;
      }
    }
    
    return true;
  }

  // 核心初始化流程
  init() {
    this.setupCanvas();
    // 根据 lazyLoad 选项决定是否自动加载图片
    // lazyLoad = false（默认）：自动加载，与 main 分支行为一致
    // lazyLoad = true：懒加载模式，需要手动调用 startLoading()
    if (!this.lazyLoad) {
      this.loadAllImgByWorker();
    }
    this.bindResizeEvents();
    console.log('[GOC] 初始化完成（' + (this.lazyLoad ? '懒加载模式：图片未加载' : '自动加载模式') + '）');
  }
  
  /**
   * 启动图片加载（手动调用，用于懒加载场景）
   * 在用户点击 Explore 按钮时调用此方法
   */
  startLoading() {
    if (this.isInitialLoadStarted) {
      console.log('[GOC] 图片已开始加载，跳过');
      return;
    }
    this.isInitialLoadStarted = true;
    console.log('[GOC] 开始加载图片序列（懒加载触发）');
    this.loadAllImgByWorker();
  }

  debounce(fn, delay) {
    let timer;
    return () => {
      clearTimeout(timer);
      timer = setTimeout(fn, delay);
    };
  }

  bindResizeEvents() {
    this.resizeHandler = this.debounce(this.handleViewportChange.bind(this), 200);
    window.addEventListener('resize', this.resizeHandler);
  }
  
  // 生成 Worker 代码（根据是否支持 ImageBitmap）
  generateWorkerCode() {
    if (this.supportsImageBitmap) {
      // ImageBitmap 模式：在 Worker 中完成解码，使用 Transferable 传输
      return `
        self.onmessage = async (event) => {
          try {
            const response = await fetch(event.data.url);
            if (!response.ok) throw new Error(\`HTTP error! status: \${response.status}\`); 
            const blob = await response.blob();
            
            // 在 Worker 中创建 ImageBitmap（解码在后台完成，不阻塞主线程）
            const bitmap = await createImageBitmap(blob);
            
            // 使用 Transferable 传输（零拷贝）
            self.postMessage({
              bitmap: bitmap,
              index: event.data.index
            }, [bitmap]);
            
          } catch (error) {
            console.error('Worker Error:', error);
            self.postMessage({
              error: error.message,
              index: event.data.index
            });
          }
        };
      `;
    } else {
      // 回退模式：传输 Blob，主线程创建 Image
      return `
        self.onmessage = async (event) => {
          try {
            const response = await fetch(event.data.url);
            if (!response.ok) throw new Error(\`HTTP error! status: \${response.status}\`); 
            const blob = await response.blob();
            self.postMessage({
              Blob: blob,
              index: event.data.index
            }); 
          } catch (error) {
            console.error('Worker Error:', error);
            self.postMessage({
              error: error.message,
              index: event.data.index
            });
          }
        };
      `;
    }
  }

  loadAllImgByWorker() {
    if (!window.Worker) {
      console.error('Browser does not support Web Workers');
      return;
    }

    // 如果已有 worker，则先终止并置为 null
    if (this.imageLoaderWorker) {
      this.imageLoaderWorker.terminate();
      this.imageLoaderWorker = null;
    }

    try {
      const workerCode = this.generateWorkerCode();
      const blob = new Blob([workerCode], { type: 'application/javascript' });
      const workerUrl = URL.createObjectURL(blob);
      
      this.imageLoaderWorker = new Worker(workerUrl);
      
      // 根据当前设备类型选择正确的 URL 列表
      const urlList = this.isPc ? this.pcImageUrlList : this.mobImageUrlList;
      let loadedCount = 0;
      let errorCount = 0;
      
      console.log('[GOC] loadAllImgByWorker 开始:', { 
        isPc: this.isPc, 
        totalImages: urlList.length,
        useImageBitmap: this.supportsImageBitmap 
      });

      // 分批发送加载请求（内存优化：避免并发过多导致内存峰值）
      const batchSize = this.FIRST_LOAD_BATCH_SIZE;
      let batchIndex = 0;
      
      const sendBatch = () => {
        const startIdx = batchIndex * batchSize;
        const endIdx = Math.min(startIdx + batchSize, urlList.length);
        
        for (let i = startIdx; i < endIdx; i++) {
          this.imageLoaderWorker.postMessage({
            url: urlList[i],
            index: i
          });
        }
        
        batchIndex++;
        
        // 如果还有更多批次，延迟发送（让 GC 有时间工作）
        if (endIdx < urlList.length) {
          setTimeout(sendBatch, this.BATCH_DELAY);
        }
      };
      
      sendBatch();

      this.imageLoaderWorker.onmessage = ({ data }) => {
        if (data.error) {
          console.error(`Error loading image ${data.index}:`, data.error);
          errorCount++;
          if (errorCount + loadedCount === urlList.length) {
            this.imageLoaderWorker.terminate();
            this.imageLoaderWorker = null;
            URL.revokeObjectURL(workerUrl);
          }
          return;
        }

        // 根据模式处理图片
        if (this.supportsImageBitmap && data.bitmap) {
          // ImageBitmap 模式：直接使用
          this.imgHtmlList[data.index] = data.bitmap;
          loadedCount++;
          
          if (this.imgHtmlList.length === urlList.length && this.isOnceUpdateImg) {
            this.isOnceUpdateImg = false;
            this.renderFirstFrame();
          }
          
          if (loadedCount + errorCount === urlList.length) {
            this.imageLoaderWorker.terminate();
            this.imageLoaderWorker = null;
            URL.revokeObjectURL(workerUrl);
            
            // 同步到预加载缓存（内存优化：使用引用而非复制）
            const firstImageUrl = this.isPc ? this.pcImageUrlList[0] : this.mobImageUrlList[0];
            if (firstImageUrl && !this.preloadedColorImages[firstImageUrl]) {
              // 直接引用，不再使用 .slice() 复制（避免双倍内存）
              this.preloadedColorImages[firstImageUrl] = this.imgHtmlList;
              console.log('[GOC] 首次加载完成，已同步到预加载缓存:', firstImageUrl.substring(0, 50));
            }
            
            // 触发首次加载完成回调（用于启动预加载其他颜色）
            if (this.onFirstLoadComplete) {
              this.onFirstLoadComplete();
            }
          }
        } else if (data.Blob) {
          // 回退模式：使用 Image 对象
          const imageUrl = URL.createObjectURL(data.Blob);
          const img = new Image();
          img.style.objectFit = "cover";
          img.src = imageUrl;
          img.onload = () => {
            this.imgHtmlList[data.index] = img;
            loadedCount++;
            if (this.imgHtmlList.length === urlList.length && this.isOnceUpdateImg) {
              this.isOnceUpdateImg = false;
              this.renderFirstFrame();
            }
            URL.revokeObjectURL(imageUrl);
            if (loadedCount + errorCount === urlList.length) {
              this.imageLoaderWorker.terminate();
              this.imageLoaderWorker = null;
              URL.revokeObjectURL(workerUrl);
              
              // 同步到预加载缓存（内存优化：使用引用而非复制）
              const firstImageUrl = this.isPc ? this.pcImageUrlList[0] : this.mobImageUrlList[0];
              if (firstImageUrl && !this.preloadedColorImages[firstImageUrl]) {
                // 直接引用，不再使用 .slice() 复制（避免双倍内存）
                this.preloadedColorImages[firstImageUrl] = this.imgHtmlList;
                // 更新 LRU 顺序并检查缓存限制
                this.updateCacheOrder(firstImageUrl);
                this.enforceCacheLimit();
                console.log('[GOC] 首次加载完成，已同步到预加载缓存:', firstImageUrl.substring(0, 50));
              }
              
              // 触发首次加载完成回调
              if (this.onFirstLoadComplete) {
                this.onFirstLoadComplete();
              }
            }
          };
          img.onerror = () => {
            console.error(`Failed to load image ${data.index}`);
            errorCount++;
            URL.revokeObjectURL(imageUrl);
            if (loadedCount + errorCount === urlList.length) {
              this.imageLoaderWorker.terminate();
              this.imageLoaderWorker = null;
              URL.revokeObjectURL(workerUrl);
            }
          };
        }
      };

      this.imageLoaderWorker.onerror = (error) => {
        console.error('Worker error:', error);
        this.imageLoaderWorker.terminate();
        this.imageLoaderWorker = null;
        URL.revokeObjectURL(workerUrl);
      };
    } catch (err) {
      console.error('loadAllImgByWorker Error', err);
    }
  }

  // 渲染首帧
  renderFirstFrame() {
    const currentImageUrlLength = this.isPc ? this.pcImageUrlList.length : this.mobImageUrlList.length;
    if (this.imgHtmlList.length >= currentImageUrlLength && this.isFirstRender) {
      this.isFirstRender = false;

      // 渲染第一帧
      if (!this.ctx) {
        return;
      }
      let frameIndex = 1;
      const image = this.imgHtmlList[frameIndex];
  
      try {
        this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
        this.ctx.drawImage(image, 0, 0, this.canvasWidth, this.canvasHeight);
        this.currentFrameIndex = frameIndex;
        console.log('renderFirstFrame By Canvas success');
      } catch (err) {
        console.error('renderFirstFrame By Canvas Error', err);
      }
    }
  }

  // 生成图片序列URL
  generateImageUrls(firstImageUrl, totalImages, imageUrlList) {
    if (!Boolean(firstImageUrl)) {
      return;
    }

    const url = new URL(firstImageUrl); // 解析传入的首张图片 URL
    const pathname = url.pathname; // 获取路径名
    const filename = pathname.split("/").pop(); // 获取文件名（如 image1.jpg）

    // 获取文件名前缀（去掉最后的 "1"），如 "image1.jpg" -> "image" 【需要确保首图的文件名以 "1" 结尾】
    const namePrefix = filename.slice(0, filename.lastIndexOf("1"));
    const fileExtension = filename.slice(filename.lastIndexOf(".")); // 获取文件扩展名（如 .jpg）

    // 生成完整的图片 URL 并存入 imageUrlList
    for (let index = 1; index <= totalImages; index++) {
      const imageUrl = `${url.origin}${pathname.slice(0, pathname.lastIndexOf("/") + 1)}${namePrefix}${index}${fileExtension}`;
      imageUrlList.push(imageUrl);
    }
  }

  // 初始化Canvas
  setupCanvas() {
    const canvasElement = this.getCanvasElement();
    if (!canvasElement) return;

    // 设置Canvas尺寸
    const [width, height] = this.calculateCanvasSize(canvasElement);
    this.canvasWidth = width;
    this.canvasHeight = height;

    // 获取绘图上下文（内存优化：添加性能提示）
    this.ctx = canvasElement.getContext('2d', {
      alpha: false,              // 不需要透明度，减少内存
      desynchronized: true,      // 允许异步渲染，减少 GPU 阻塞
      willReadFrequently: false  // 不频繁读取像素，启用硬件加速
    });

    // 加载图片序列配置
    const {
      pcFirstImageUrl: pcImage1,
      mobFirstImageUrl: mobImage1,
      pcImageNum: pcFrames,
      mobImageNum: mobFrames
    } = canvasElement.dataset;

    const pcImageNumCount = Number(pcFrames);
    const mobImageNumCount = Number(mobFrames);

    this.pcImageNum = pcImageNumCount;
    this.mobImageNum = mobImageNumCount;

    // 根据当前设备类型生成对应的 URL 列表（确保设备资源隔离）
    if (this.isPc) {
      this.generateImageUrls(pcImage1, pcImageNumCount, this.pcImageUrlList);
      console.log('[GOC] setupCanvas - PC 模式，URL 数量:', this.pcImageUrlList.length);
    } else {
      this.generateImageUrls(mobImage1, mobImageNumCount, this.mobImageUrlList);
      console.log('[GOC] setupCanvas - Mobile 模式，URL 数量:', this.mobImageUrlList.length);
    }
  }

  // 渲染单帧到Canvas
  renderFrame(progress) {
    if (!this.ctx) {
      return;
    }

    const isForwardScroll = progress >= this.previousProgress; // 获取当前滚动方向
    const currentImageUrlLength = this.isPc ? this.pcImageUrlList.length : this.mobImageUrlList.length;
    let frameIndex = Math.floor(progress * (currentImageUrlLength - 1));

    // 如果是反向滚动，检查索引是否减少
    if (!isForwardScroll) {
      frameIndex = Math.max(frameIndex, 0);  // 确保不会低于0
    } else {
      frameIndex = Math.min(frameIndex, currentImageUrlLength - 1);  // 确保不会超过总图片数
    }
    const image = this.imgHtmlList[frameIndex];

    if (frameIndex === this.currentFrameIndex || !image) {
      return;
    }

    try {
      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
      this.ctx.drawImage(image, 0, 0, this.canvasWidth, this.canvasHeight);
      this.previousProgress = progress; // 更新previousProgress，记录本次滚动的进度
      this.currentFrameIndex = frameIndex;
    } catch (err) {
      console.error('renderFrame By Canvas Error', err);
    }
  }

  handleViewportChange() {
    const canvasElement = this.getCanvasElement();
    if (!canvasElement) return;

    const [width, height] = this.calculateCanvasSize(canvasElement);
    this.canvasWidth = width;
    this.canvasHeight = height;

    // isPc状态变更
    const isPc = this.checkViewportWidth(this.breakpoint);
    if (isPc !== this.isPc) {
      console.log('[GOC] 设备类型变更:', { from: this.isPc ? 'PC' : 'Mobile', to: isPc ? 'PC' : 'Mobile' });
      
      // 释放旧缓存的 ImageBitmap（如果使用 ImageBitmap 模式）
      if (this.supportsImageBitmap) {
        this.releaseAllColors();
      }
      
      // 清除旧图片缓存
      this.imgHtmlList = [];
      this.isOnceUpdateImg = true;
      this.isFirstRender = true;

      this.isPc = isPc;
      this.loadAllImgByWorker();
    }
  }

  // 辅助方法
  checkViewportWidth(breakpoint) {
    return window.matchMedia(`(min-width: ${breakpoint}px)`).matches;
  }

  getCanvasElement() {
    return this.container.querySelector(
      this.isPc ? '.image-sequences-canvas-pc' : '.image-sequences-canvas-m'
    );
  }

  calculateCanvasSize(element) {
    return [element.width, element.height];
  }
  
  /**
   * 释放指定颜色的所有图片内存（仅 ImageBitmap 模式有效）
   * @param {string} colorKey - 颜色标识（URL）
   */
  releaseColor(colorKey) {
    const images = this.preloadedColorImages[colorKey];
    if (images && this.supportsImageBitmap) {
      images.forEach(img => {
        if (img && typeof img.close === 'function') {
          img.close(); // ImageBitmap.close() 立即释放内存
        }
      });
      delete this.preloadedColorImages[colorKey];
      console.log('[GOC] 已释放颜色内存:', colorKey.substring(0, 50));
    }
  }
  
  /**
   * 释放所有颜色的缓存（用于设备类型切换时）
   */
  releaseAllColors() {
    Object.keys(this.preloadedColorImages).forEach(colorKey => {
      this.releaseColor(colorKey);
    });
    this.cacheOrder = []; // 清空 LRU 顺序
    console.log('[GOC] 已释放所有颜色缓存');
  }
  
  /**
   * 完全销毁并释放所有资源（用于退出全景查看时）
   * 调用此方法后，如需重新使用，需调用 startLoading() 重新加载
   */
  destroy() {
    console.log('[GOC] destroy() 开始释放所有资源...');
    
    // 1. 释放当前显示的 imgHtmlList 中的 ImageBitmap
    if (this.imgHtmlList && this.imgHtmlList.length > 0) {
      if (this.supportsImageBitmap) {
        this.imgHtmlList.forEach(img => {
          if (img && typeof img.close === 'function') {
            img.close(); // 立即释放 GPU 内存
          }
        });
      }
      this.imgHtmlList = [];
      console.log('[GOC] imgHtmlList 已清空');
    }
    
    // 2. 释放所有预加载颜色缓存
    this.releaseAllColors();
    
    // 3. 终止主加载 Worker
    if (this.imageLoaderWorker) {
      this.imageLoaderWorker.terminate();
      this.imageLoaderWorker = null;
      console.log('[GOC] imageLoaderWorker 已终止');
    }
    
    // 4. 终止所有预加载 Worker
    Object.keys(this.preloadWorkers).forEach(colorKey => {
      if (this.preloadWorkers[colorKey]) {
        this.preloadWorkers[colorKey].terminate();
      }
    });
    this.preloadWorkers = {};
    console.log('[GOC] 所有预加载 Worker 已终止');
    
    // 5. 清空 Canvas（释放 GPU 纹理）
    if (this.ctx) {
      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
      console.log('[GOC] Canvas 已清空');
    }
    
    // 6. 重置状态（允许下次重新加载）
    this.isOnceUpdateImg = true;
    this.isFirstRender = true;
    this.isInitialLoadStarted = false;
    this.currentColorKey = null;
    this.currentFrameIndex = 0;
    this.previousProgress = 0;
    this.pcImageUrlList = [];
    this.mobImageUrlList = [];
    
    console.log('[GOC] destroy() 完成，所有资源已释放');
  }
  
  /**
   * 更新 LRU 缓存顺序（将 colorKey 移到最后，表示最近使用）
   * @param {string} colorKey - 颜色标识
   */
  updateCacheOrder(colorKey) {
    // 从 cacheOrder 中移除（如果存在）
    const index = this.cacheOrder.indexOf(colorKey);
    if (index > -1) {
      this.cacheOrder.splice(index, 1);
    }
    // 添加到末尾（最近使用）
    this.cacheOrder.push(colorKey);
    console.log('[GOC] LRU 顺序更新:', this.cacheOrder.map(k => k.substring(0, 30)));
  }
  
  /**
   * 强制执行缓存限制（如果超出 maxCachedColors，释放最旧的颜色）
   * 注意：当前正在使用的颜色（currentColorKey）不会被释放
   */
  enforceCacheLimit() {
    // 计算实际缓存数量（排除当前使用的颜色）
    const effectiveCacheCount = this.cacheOrder.filter(key => key !== this.currentColorKey).length;
    const targetCount = this.maxCachedColors;
    
    // 只释放非当前使用的颜色
    while (effectiveCacheCount > targetCount && this.cacheOrder.length > 0) {
      // 找到最早的、非当前使用的颜色
      const oldestIndex = this.cacheOrder.findIndex(key => key !== this.currentColorKey);
      if (oldestIndex === -1) break; // 如果只剩当前颜色，不释放
      
      const oldestColorKey = this.cacheOrder.splice(oldestIndex, 1)[0];
      console.log('[GOC] 缓存超限，释放非当前颜色:', oldestColorKey.substring(0, 50));
      this.releaseColor(oldestColorKey);
      break; // 每次只释放一个，避免过度释放
    }
  }

  /**
   * 切换颜色序列 - 动态加载新的颜色图片
   * @param {Object} config - 配置对象
   * @param {string} config.pcFirstImageUrl - PC 端首张图片 URL
   * @param {string} config.mobFirstImageUrl - 移动端首张图片 URL
   * @param {number} config.imageNum - 图片数量
   * @param {Function} onComplete - 加载完成回调
   */
  switchColor(config, onComplete) {
    const { pcFirstImageUrl, mobFirstImageUrl, imageNum } = config;
    
    console.log('[GOC] switchColor 开始:', { 
      pcFirstImageUrl: pcFirstImageUrl.substring(0, 50), 
      mobFirstImageUrl: mobFirstImageUrl.substring(0, 50), 
      imageNum, 
      isPc: this.isPc 
    });
    
    // 根据当前设备类型选择正确的 colorKey（确保设备资源隔离）
    const colorKey = this.isPc ? pcFirstImageUrl : mobFirstImageUrl;
    
    // 优先使用预加载缓存（如果该颜色已预加载完成）
    if (this.preloadedColorImages[colorKey] && 
        this.preloadedColorImages[colorKey].length >= imageNum) {
      console.log('[GOC] 使用预加载缓存切换颜色:', { 
        colorKey: colorKey.substring(0, 50), 
        imageCount: this.preloadedColorImages[colorKey].length 
      });
      
      // 更新 LRU 顺序
      this.updateCacheOrder(colorKey);
      
      // 更新当前颜色标记（在释放旧颜色之前）
      const oldColorKey = this.currentColorKey;
      this.currentColorKey = colorKey;
      
      // 释放旧颜色缓存（如果不是同一个颜色）
      if (oldColorKey && oldColorKey !== colorKey && this.preloadedColorImages[oldColorKey]) {
        console.log('[GOC] 释放旧颜色缓存:', oldColorKey.substring(0, 50));
        this.releaseColor(oldColorKey);
        const idx = this.cacheOrder.indexOf(oldColorKey);
        if (idx > -1) this.cacheOrder.splice(idx, 1);
      }
      
      // 直接使用预加载的图片缓存（内存优化：使用引用而非复制）
      this.imgHtmlList = this.preloadedColorImages[colorKey]; // 直接引用，不复制
      this.isOnceUpdateImg = false;
      this.isFirstRender = true;
      this.currentFrameIndex = 0;
      this.previousProgress = 0;
      
      // 渲染首帧
      this.renderFirstFrame();
      
      // 立即触发回调
      if (onComplete) onComplete();
      return;
    }
    
    // 如果没有预加载缓存，则走正常加载流程
    console.log('[GOC] 无预加载缓存，开始正常加载');
    
    // 0. 在加载新颜色前，先释放旧颜色的缓存（内存优化）
    if (this.currentColorKey && this.preloadedColorImages[this.currentColorKey]) {
      console.log('[GOC] 释放旧颜色缓存:', this.currentColorKey.substring(0, 50));
      this.releaseColor(this.currentColorKey);
      // 从 cacheOrder 中移除
      const idx = this.cacheOrder.indexOf(this.currentColorKey);
      if (idx > -1) this.cacheOrder.splice(idx, 1);
    }
    
    // 更新当前颜色标记
    this.currentColorKey = colorKey;
    
    // 1. 终止当前 Worker
    if (this.imageLoaderWorker) {
      this.imageLoaderWorker.terminate();
      this.imageLoaderWorker = null;
    }

    // 2. 清空当前图片缓存
    this.imgHtmlList = [];
    this.isOnceUpdateImg = true;
    this.isFirstRender = true;
    this.currentFrameIndex = 0;
    this.previousProgress = 0;
    
    // 3. 清空 canvas，避免显示旧颜色内容
    if (this.ctx) {
      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
      console.log('[GOC] 已清空 canvas');
    }

    // 3. 清空并重新生成 URL 列表（根据当前设备类型）
    this.pcImageUrlList = [];
    this.mobImageUrlList = [];

    if (this.isPc) {
      this.pcImageNum = imageNum;
      this.generateImageUrls(pcFirstImageUrl, imageNum, this.pcImageUrlList);
      console.log('[GOC] 生成 PC URL 列表:', this.pcImageUrlList.length);
    } else {
      this.mobImageNum = imageNum;
      this.generateImageUrls(mobFirstImageUrl, imageNum, this.mobImageUrlList);
      console.log('[GOC] 生成 Mobile URL 列表:', this.mobImageUrlList.length);
    }

    // 4. 保存回调函数
    this.onColorSwitchComplete = onComplete;

    // 5. 重新加载图片（首次加载使用并发，不分批）
    this.loadColorImages();
  }

  // 加载颜色切换的图片（带回调支持，并发加载）
  loadColorImages() {
    if (!window.Worker) {
      console.error('Browser does not support Web Workers');
      if (this.onColorSwitchComplete) this.onColorSwitchComplete();
      return;
    }

    try {
      const workerCode = this.generateWorkerCode();
      const blob = new Blob([workerCode], { type: 'application/javascript' });
      const workerUrl = URL.createObjectURL(blob);
      
      this.imageLoaderWorker = new Worker(workerUrl);
      const urlList = this.isPc ? this.pcImageUrlList : this.mobImageUrlList;
      const totalImages = urlList.length;
      let loadedCount = 0;
      let errorCount = 0;

      console.log('[GOC] loadColorImages 开始加载:', { 
        totalImages, 
        isPc: this.isPc,
        useImageBitmap: this.supportsImageBitmap 
      });
      
      const loadStartTime = performance.now();

      if (totalImages === 0) {
        console.error('[GOC] URL 列表为空!');
        if (this.onColorSwitchComplete) this.onColorSwitchComplete();
        return;
      }

      this.imageLoaderWorker.onmessage = ({ data }) => {
        if (data.error) {
          console.error(`Error loading image ${data.index}:`, data.error);
          errorCount++;
          this.checkColorLoadComplete(loadedCount, errorCount, totalImages, workerUrl, loadStartTime);
          return;
        }

        // 根据模式处理图片
        if (this.supportsImageBitmap && data.bitmap) {
          this.imgHtmlList[data.index] = data.bitmap;
          loadedCount++;
          
          // 渲染首帧
          if (this.imgHtmlList.length >= totalImages && this.isOnceUpdateImg) {
            this.isOnceUpdateImg = false;
            this.renderFirstFrame();
          }
          
          this.checkColorLoadComplete(loadedCount, errorCount, totalImages, workerUrl, loadStartTime);
        } else if (data.Blob) {
          const imageUrl = URL.createObjectURL(data.Blob);
          const img = new Image();
          img.style.objectFit = "cover";
          img.src = imageUrl;
          img.onload = () => {
            this.imgHtmlList[data.index] = img;
            loadedCount++;
            
            // 渲染首帧
            if (this.imgHtmlList.length >= totalImages && this.isOnceUpdateImg) {
              this.isOnceUpdateImg = false;
              this.renderFirstFrame();
            }
            
            URL.revokeObjectURL(imageUrl);
            this.checkColorLoadComplete(loadedCount, errorCount, totalImages, workerUrl, loadStartTime);
          };
          img.onerror = () => {
            console.error(`Failed to load image ${data.index}`);
            errorCount++;
            URL.revokeObjectURL(imageUrl);
            this.checkColorLoadComplete(loadedCount, errorCount, totalImages, workerUrl, loadStartTime);
          };
        }
      };

      this.imageLoaderWorker.onerror = (error) => {
        console.error('Worker error:', error);
        this.imageLoaderWorker.terminate();
        this.imageLoaderWorker = null;
        URL.revokeObjectURL(workerUrl);
        if (this.onColorSwitchComplete) this.onColorSwitchComplete();
      };

      // 分批发送加载请求（内存优化：避免并发过多导致内存峰值）
      console.log('[GOC] 开始分批发送 postMessage 到 Worker');
      const batchSize = this.FIRST_LOAD_BATCH_SIZE;
      let batchIdx = 0;
      
      const sendColorBatch = () => {
        const startIdx = batchIdx * batchSize;
        const endIdx = Math.min(startIdx + batchSize, totalImages);
        
        for (let i = startIdx; i < endIdx; i++) {
          this.imageLoaderWorker.postMessage({
            url: urlList[i],
            index: i
          });
        }
        
        batchIdx++;
        
        // 如果还有更多批次，延迟发送（让 GC 有时间工作）
        if (endIdx < totalImages) {
          setTimeout(sendColorBatch, this.BATCH_DELAY);
        }
      };
      
      sendColorBatch();
      console.log('[GOC] 分批加载已启动');
    } catch (err) {
      console.error('loadColorImages Error', err);
      if (this.onColorSwitchComplete) this.onColorSwitchComplete();
    }
  }

  // 检查颜色加载是否完成
  checkColorLoadComplete(loadedCount, errorCount, totalImages, workerUrl, startTime) {
    // 每 20 张打印一次进度
    if ((loadedCount + errorCount) % 20 === 0 || loadedCount + errorCount === totalImages) {
      const elapsed = ((performance.now() - startTime) / 1000).toFixed(2);
      console.log('[GOC] 加载进度:', { loadedCount, errorCount, totalImages, time: `${elapsed}s` });
    }
    
    if (loadedCount + errorCount === totalImages) {
      const totalElapsed = ((performance.now() - startTime) / 1000).toFixed(2);
      console.log(`[GOC] 加载完成，总耗时: ${totalElapsed}s`);
      this.imageLoaderWorker.terminate();
      this.imageLoaderWorker = null;
      URL.revokeObjectURL(workerUrl);
      
      // 同步到预加载缓存（内存优化：使用引用而非复制）
      const colorKey = this.isPc ? this.pcImageUrlList[0] : this.mobImageUrlList[0];
      if (colorKey && !this.preloadedColorImages[colorKey]) {
        // 直接引用，不再使用 .slice() 复制（避免双倍内存）
        this.preloadedColorImages[colorKey] = this.imgHtmlList;
        // 更新 LRU 顺序并检查缓存限制
        this.updateCacheOrder(colorKey);
        this.enforceCacheLimit();
        console.log('[GOC] 颜色加载完成，已同步到缓存:', colorKey.substring(0, 50));
      }
      
      // 触发回调
      if (this.onColorSwitchComplete) {
        this.onColorSwitchComplete();
        this.onColorSwitchComplete = null;
      }
    }
  }

  /**
   * 预加载颜色序列 - 后台静默加载（使用批次加载，不影响当前显示）
   * @param {Object} config - 配置对象
   * @param {string} config.pcFirstImageUrl - PC 端首张图片 URL
   * @param {string} config.mobFirstImageUrl - 移动端首张图片 URL
   * @param {number} config.imageNum - 图片数量
   * @param {Function} onComplete - 加载完成回调
   */
  preloadColor(config, onComplete) {
    const { pcFirstImageUrl, mobFirstImageUrl, imageNum } = config;
    
    // 根据当前设备类型选择正确的 colorKey（确保设备资源隔离）
    const colorKey = this.isPc ? pcFirstImageUrl : mobFirstImageUrl;
    
    console.log('[GOC] preloadColor 开始:', { 
      colorKey: colorKey.substring(0, 50), 
      imageNum, 
      isPc: this.isPc 
    });
    
    // 如果该颜色已预加载完成，直接回调
    if (this.preloadedColorImages[colorKey] && this.preloadedColorImages[colorKey].length >= imageNum) {
      console.log('[GOC] 颜色已预加载完成:', colorKey.substring(0, 50));
      if (onComplete) onComplete();
      return;
    }
    
    // 如果正在预加载中，不重复启动
    if (this.preloadWorkers[colorKey]) {
      console.log('[GOC] 颜色正在预加载中:', colorKey.substring(0, 50));
      return;
    }
    
    // 生成 URL 列表（根据当前设备类型）
    const urlList = [];
    this.generateImageUrls(colorKey, imageNum, urlList);
    
    // 初始化预加载缓存
    this.preloadedColorImages[colorKey] = [];
    
    if (!window.Worker) {
      console.error('Browser does not support Web Workers');
      if (onComplete) onComplete();
      return;
    }
    
    try {
      const workerCode = this.generateWorkerCode();
      const blob = new Blob([workerCode], { type: 'application/javascript' });
      const workerUrl = URL.createObjectURL(blob);
      
      const worker = new Worker(workerUrl);
      this.preloadWorkers[colorKey] = worker;
      
      const totalImages = urlList.length;
      let loadedCount = 0;
      let errorCount = 0;
      let batchIndex = 0;
      const preloadedImages = this.preloadedColorImages[colorKey];
      
      console.log('[GOC] preloadColor 开始批次加载:', { totalImages, batchSize: this.BATCH_SIZE });
      const loadStartTime = performance.now();
      
      if (totalImages === 0) {
        console.error('[GOC] 预加载 URL 列表为空!');
        if (onComplete) onComplete();
        return;
      }
      
      worker.onmessage = ({ data }) => {
        if (data.error) {
          console.error(`[GOC] 预加载图片错误 ${data.index}:`, data.error);
          errorCount++;
          this.checkPreloadComplete(colorKey, loadedCount, errorCount, totalImages, workerUrl, loadStartTime, onComplete);
          return;
        }
        
        // 根据模式处理图片
        if (this.supportsImageBitmap && data.bitmap) {
          preloadedImages[data.index] = data.bitmap;
          loadedCount++;
          this.checkPreloadComplete(colorKey, loadedCount, errorCount, totalImages, workerUrl, loadStartTime, onComplete);
        } else if (data.Blob) {
          const imageUrl = URL.createObjectURL(data.Blob);
          const img = new Image();
          img.style.objectFit = "cover";
          img.src = imageUrl;
          img.onload = () => {
            preloadedImages[data.index] = img;
            loadedCount++;
            URL.revokeObjectURL(imageUrl);
            this.checkPreloadComplete(colorKey, loadedCount, errorCount, totalImages, workerUrl, loadStartTime, onComplete);
          };
          img.onerror = () => {
            console.error(`[GOC] 预加载图片失败 ${data.index}`);
            errorCount++;
            URL.revokeObjectURL(imageUrl);
            this.checkPreloadComplete(colorKey, loadedCount, errorCount, totalImages, workerUrl, loadStartTime, onComplete);
          };
        }
      };
      
      worker.onerror = (error) => {
        console.error('[GOC] 预加载 Worker 错误:', error);
        worker.terminate();
        delete this.preloadWorkers[colorKey];
        URL.revokeObjectURL(workerUrl);
        if (onComplete) onComplete();
      };
      
      // 批次发送加载请求
      const sendBatch = () => {
        const startIdx = batchIndex * this.BATCH_SIZE;
        const endIdx = Math.min(startIdx + this.BATCH_SIZE, totalImages);
        
        for (let i = startIdx; i < endIdx; i++) {
          worker.postMessage({ url: urlList[i], index: i });
        }
        
        batchIndex++;
        
        // 如果还有更多批次，延迟发送
        if (endIdx < totalImages) {
          setTimeout(sendBatch, this.BATCH_DELAY);
        }
      };
      
      // 开始发送第一批
      sendBatch();
      console.log('[GOC] 预加载批次请求已启动');
      
    } catch (err) {
      console.error('[GOC] preloadColor Error', err);
      if (onComplete) onComplete();
    }
  }
  
  // 检查预加载是否完成
  checkPreloadComplete(colorKey, loadedCount, errorCount, totalImages, workerUrl, startTime, onComplete) {
    // 每 40 张打印一次进度（预加载频率低一些，减少日志）
    if ((loadedCount + errorCount) % 40 === 0 || loadedCount + errorCount === totalImages) {
      const elapsed = ((performance.now() - startTime) / 1000).toFixed(2);
      console.log('[GOC] 预加载进度:', { colorKey: colorKey.substring(0, 50), loadedCount, errorCount, totalImages, time: `${elapsed}s` });
    }
    
    if (loadedCount + errorCount === totalImages) {
      const totalElapsed = ((performance.now() - startTime) / 1000).toFixed(2);
      console.log(`[GOC] 预加载完成，总耗时: ${totalElapsed}s`);
      
      // 更新 LRU 顺序并检查缓存限制
      this.updateCacheOrder(colorKey);
      this.enforceCacheLimit();
      
      // 清理 Worker
      if (this.preloadWorkers[colorKey]) {
        this.preloadWorkers[colorKey].terminate();
        delete this.preloadWorkers[colorKey];
      }
      URL.revokeObjectURL(workerUrl);
      
      // 触发回调
      if (onComplete) onComplete();
    }
  }
  
  /**
   * 取消指定颜色的预加载（用于用户快速切换颜色时，取消之前的预加载以节省资源）
   * @param {Object} config - 配置对象，与 preloadColor 参数相同
   * @returns {boolean} - 是否成功取消
   */
  cancelPreload(config) {
    const { pcFirstImageUrl, mobFirstImageUrl } = config;
    const colorKey = this.isPc ? pcFirstImageUrl : mobFirstImageUrl;
    
    if (this.preloadWorkers[colorKey]) {
      console.log('[GOC] 取消预加载 Worker:', colorKey.substring(0, 50));
      this.preloadWorkers[colorKey].terminate();
      delete this.preloadWorkers[colorKey];
      
      // 清除未完成的预加载缓存（释放内存）
      if (this.preloadedColorImages[colorKey]) {
        const images = this.preloadedColorImages[colorKey];
        if (this.supportsImageBitmap) {
          images.forEach(img => {
            if (img && typeof img.close === 'function') {
              img.close();
            }
          });
        }
        delete this.preloadedColorImages[colorKey];
      }
      return true;
    }
    return false;
  }
  
  /**
   * 使用预加载的图片切换颜色（快速切换）
   * 
   * 注意：此方法为预留接口，当前 switchColor 方法已内置预加载缓存检查逻辑，
   * 因此在大多数场景下无需直接调用此方法。保留此方法是为了：
   * 1. 提供更细粒度的控制，允许外部代码直接使用预加载缓存
   * 2. 作为 switchColor 内部逻辑的可复用组件
   * 
   * @param {string} colorKey - 颜色标识（URL，与 preloadColor 中使用的 colorKey 相同）
   * @returns {boolean} - 是否成功使用预加载缓存切换颜色
   */
  switchToPreloadedColor(colorKey) {
    const preloadedImages = this.preloadedColorImages[colorKey];
    if (!preloadedImages || preloadedImages.length === 0) {
      return false;
    }
    
    console.log('[GOC] 使用预加载的图片:', { colorKey: colorKey.substring(0, 50), imageCount: preloadedImages.length });
    
    // 直接使用预加载的图片（内存优化：使用引用而非复制）
    this.imgHtmlList = preloadedImages; // 直接引用，不复制
    this.isOnceUpdateImg = false;
    this.isFirstRender = true;
    this.currentFrameIndex = 0;
    this.previousProgress = 0;
    
    // 渲染首帧
    this.renderFirstFrame();
    
    return true;
  }
  
  /**
   * 检查指定颜色是否已预加载完成
   * @param {Object} config - 配置对象
   * @returns {boolean} - 是否已预加载完成
   */
  isColorPreloaded(config) {
    const { pcFirstImageUrl, mobFirstImageUrl, imageNum } = config;
    const colorKey = this.isPc ? pcFirstImageUrl : mobFirstImageUrl;
    return this.preloadedColorImages[colorKey] && 
           this.preloadedColorImages[colorKey].length >= imageNum;
  }
  
  /**
   * 检查指定颜色是否正在预加载中
   * @param {Object} config - 配置对象
   * @returns {boolean} - 是否正在预加载
   */
  isColorPreloading(config) {
    const { pcFirstImageUrl, mobFirstImageUrl } = config;
    const colorKey = this.isPc ? pcFirstImageUrl : mobFirstImageUrl;
    return !!this.preloadWorkers[colorKey];
  }
}